import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Box,
  Container,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  Badge,
  Button,
} from "@mui/material";
import {
  Search as SearchIcon,
  Login as LoginIcon,
  Sync as SyncIcon,
  TrendingUp as ProgressIcon,
  Close as CloseIcon,
  Apps as AppsIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSearchTerm,
  setKindFilter,
  setSsoFilter,
  setCapabilityFilter,
  setSelectedApp,
  clearSelectedApp,
} from "../redux/slices/appRegistrySlice";
import { fetchApps, fetchScriptsForApp } from "../redux/thunks/appRegistryThunks";

const KIND_COLORS = {
  education: "primary",
  assessment: "warning",
  career: "success",
  language: "info",
  coding: "secondary",
  certification: "error",
  library: "default",
  math: "primary",
  other: "default",
};

const SSO_LABELS = {
  none: "None",
  password: "Password",
  saml: "SAML",
  oauth: "OAuth",
  lti: "LTI",
  custom_script: "Script",
};

const AppRegistry = () => {
  const dispatch = useDispatch();
  const {
    apps,
    selectedApp,
    scripts,
    searchTerm,
    kindFilter,
    ssoFilter,
    capabilityFilter,
    loading,
    error,
  } = useSelector((state) => state.appRegistry);

  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchApps());
  }, [dispatch]);

  const filteredApps = useMemo(() => {
    let filtered = [...apps];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.name.toLowerCase().includes(term) ||
          app.configKey.toLowerCase().includes(term) ||
          (app.description && app.description.toLowerCase().includes(term)) ||
          (app.tags && app.tags.some((tag) => tag.toLowerCase().includes(term)))
      );
    }

    if (kindFilter !== "all") {
      filtered = filtered.filter((app) => app.kind === kindFilter);
    }

    if (ssoFilter !== "all") {
      filtered = filtered.filter((app) => app.ssoType === ssoFilter);
    }

    if (capabilityFilter !== "all") {
      switch (capabilityFilter) {
        case "login":
          filtered = filtered.filter((app) => app.hasLoginScript);
          break;
        case "roster":
          filtered = filtered.filter((app) => app.hasRosterSync);
          break;
        case "progress":
          filtered = filtered.filter((app) => app.hasProgressCollection);
          break;
        default:
          break;
      }
    }

    return filtered;
  }, [apps, searchTerm, kindFilter, ssoFilter, capabilityFilter]);

  const handleRowClick = (app) => {
    dispatch(setSelectedApp(app));
    dispatch(fetchScriptsForApp(app.id));
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    dispatch(clearSelectedApp());
  };

  if (loading && apps.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <AppsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          App Registry
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {apps.length} applications registered
        </Typography>
      </Box>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search apps..."
          value={searchTerm}
          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Kind</InputLabel>
          <Select
            value={kindFilter}
            label="Kind"
            onChange={(e) => dispatch(setKindFilter(e.target.value))}
          >
            <MenuItem value="all">All Kinds</MenuItem>
            <MenuItem value="education">Education</MenuItem>
            <MenuItem value="assessment">Assessment</MenuItem>
            <MenuItem value="career">Career</MenuItem>
            <MenuItem value="language">Language</MenuItem>
            <MenuItem value="coding">Coding</MenuItem>
            <MenuItem value="certification">Certification</MenuItem>
            <MenuItem value="library">Library</MenuItem>
            <MenuItem value="math">Math</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>SSO Type</InputLabel>
          <Select
            value={ssoFilter}
            label="SSO Type"
            onChange={(e) => dispatch(setSsoFilter(e.target.value))}
          >
            <MenuItem value="all">All SSO</MenuItem>
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="password">Password</MenuItem>
            <MenuItem value="saml">SAML</MenuItem>
            <MenuItem value="oauth">OAuth</MenuItem>
            <MenuItem value="lti">LTI</MenuItem>
            <MenuItem value="custom_script">Custom Script</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Capability</InputLabel>
          <Select
            value={capabilityFilter}
            label="Capability"
            onChange={(e) => dispatch(setCapabilityFilter(e.target.value))}
          >
            <MenuItem value="all">All Capabilities</MenuItem>
            <MenuItem value="login">Has Login Script</MenuItem>
            <MenuItem value="roster">Has Roster Sync</MenuItem>
            <MenuItem value="progress">Has Progress</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell><strong>Application</strong></TableCell>
              <TableCell><strong>Config Key</strong></TableCell>
              <TableCell><strong>Kind</strong></TableCell>
              <TableCell><strong>SSO</strong></TableCell>
              <TableCell align="center"><strong>Capabilities</strong></TableCell>
              <TableCell><strong>Tags</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredApps.length > 0 ? (
              filteredApps.map((app) => (
                <TableRow
                  key={app.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(app)}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {app.displayName || app.name}
                      </Typography>
                      {app.shortName && (
                        <Typography variant="caption" color="text.secondary">
                          {app.shortName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                      {app.configKey}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={app.kind}
                      size="small"
                      color={KIND_COLORS[app.kind] || "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SSO_LABELS[app.ssoType] || app.ssoType}
                      size="small"
                      variant="filled"
                      sx={{ fontSize: "0.75rem" }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Login Script">
                        <Badge color="success" variant="dot" invisible={!app.hasLoginScript}>
                          <LoginIcon
                            fontSize="small"
                            color={app.hasLoginScript ? "action" : "disabled"}
                          />
                        </Badge>
                      </Tooltip>
                      <Tooltip title="Roster Sync">
                        <Badge color="success" variant="dot" invisible={!app.hasRosterSync}>
                          <SyncIcon
                            fontSize="small"
                            color={app.hasRosterSync ? "action" : "disabled"}
                          />
                        </Badge>
                      </Tooltip>
                      <Tooltip title="Progress Collection">
                        <Badge color="success" variant="dot" invisible={!app.hasProgressCollection}>
                          <ProgressIcon
                            fontSize="small"
                            color={app.hasProgressCollection ? "action" : "disabled"}
                          />
                        </Badge>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {app.tags &&
                        app.tags.slice(0, 3).map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                        ))}
                      {app.tags && app.tags.length > 3 && (
                        <Chip label={`+${app.tags.length - 3}`} size="small" sx={{ fontSize: "0.7rem" }} />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No applications found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{ "& .MuiDrawer-paper": { width: { xs: "100%", sm: 480 } } }}
      >
        {selectedApp && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h5">{selectedApp.displayName || selectedApp.name}</Typography>
              <IconButton onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                handleCloseDrawer();
                navigate(`/apps/${selectedApp.configKey}`);
              }}
              sx={{ mb: 2 }}
            >
              View Full Detail
            </Button>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip label={selectedApp.kind} color={KIND_COLORS[selectedApp.kind] || "default"} size="small" />
              <Chip label={SSO_LABELS[selectedApp.ssoType] || selectedApp.ssoType} size="small" />
              {selectedApp.isActive ? (
                <Chip label="Active" color="success" size="small" variant="outlined" />
              ) : (
                <Chip label="Inactive" color="error" size="small" variant="outlined" />
              )}
            </Stack>

            {selectedApp.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedApp.description}
              </Typography>
            )}

            {selectedApp.linkAddress && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                <a href={selectedApp.linkAddress} target="_blank" rel="noopener noreferrer">
                  {selectedApp.linkAddress} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: "middle" }} />
                </a>
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Details
            </Typography>
            <List dense disablePadding>
              <ListItem disableGutters>
                <ListItemText primary="Config Key" secondary={selectedApp.configKey} />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText primary="Short Name" secondary={selectedApp.shortName || "N/A"} />
              </ListItem>
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Capabilities
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                icon={<LoginIcon />}
                label="Login"
                color={selectedApp.hasLoginScript ? "success" : "default"}
                variant={selectedApp.hasLoginScript ? "filled" : "outlined"}
                size="small"
              />
              <Chip
                icon={<SyncIcon />}
                label="Roster"
                color={selectedApp.hasRosterSync ? "success" : "default"}
                variant={selectedApp.hasRosterSync ? "filled" : "outlined"}
                size="small"
              />
              <Chip
                icon={<ProgressIcon />}
                label="Progress"
                color={selectedApp.hasProgressCollection ? "success" : "default"}
                variant={selectedApp.hasProgressCollection ? "filled" : "outlined"}
                size="small"
              />
            </Stack>

            {selectedApp.tags && selectedApp.tags.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selectedApp.tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </Stack>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Integration Scripts ({scripts.length})
            </Typography>
            {scripts.length > 0 ? (
              <List dense disablePadding>
                {scripts.map((script) => (
                  <ListItem key={script.id} disableGutters>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={script.type.replace(/_/g, " ")}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            label={script.executionTarget}
                            size="small"
                            variant="outlined"
                          />
                          {script.isActive ? (
                            <Chip label="Active" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Inactive" size="small" color="error" variant="outlined" />
                          )}
                        </Stack>
                      }
                      secondary={
                        <>
                          {script.cronSchedule && `Cron: ${script.cronSchedule}`}
                          {script.notes && ` | ${script.notes}`}
                          {` | v${script.version}`}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No integration scripts configured.
              </Typography>
            )}

            {selectedApp.metadata && Object.keys(selectedApp.metadata).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Metadata
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50" }}>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem" whiteSpace="pre-wrap">
                    {JSON.stringify(selectedApp.metadata, null, 2)}
                  </Typography>
                </Paper>
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Container>
  );
};

export default AppRegistry;
